import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Table, Button, Card, Space, Typography, Empty, Descriptions, Tag, Grid } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { fetchPayouts } from '../../store/payoutsSlice';
import * as XLSX from 'xlsx';
import { formatCurrency } from '../../utils/formatters';

const { Title, Text } = Typography;

const PayoutHistory = () => {
  const dispatch = useDispatch();

  // Redux selectors
  const { user } = useSelector((state) => state.auth);
  const { payouts, loading: payoutsLoading } = useSelector((state) => state.payouts);

  const id = user?.id;

  // Initial Data Fetch
  useEffect(() => {
    if (id) {
      dispatch(fetchPayouts(id));
    }
  }, [dispatch, id]);

  // Handle Excel Export
  const handleExport = () => {
    if (!payouts || payouts.length === 0) return;

    const data = payouts.map((p) => ({
      'Date': new Date(p.date).toLocaleString(),
      'Amount': p.amount,
      'Transaction ID': p.transactionId || '-',
      'Notes': p.notes || '-',
      'BDE Name': user?.name || id,
      'BDE ID': id
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Payout History");
    XLSX.writeFile(wb, `My_Payout_History_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Table Columns
  const screens = Grid.useBreakpoint();
  const columns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (text) => new Date(text).toLocaleString(),
      sorter: (a, b) => new Date(a.date) - new Date(b.date),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => <Text strong type="success">{formatCurrency(amount)}</Text>,
      sorter: (a, b) => a.amount - b.amount,
    },
    {
      title: 'Transaction ID',
      dataIndex: 'transactionId',
      key: 'transactionId',
      render: (text) => text || <Text type="secondary">-</Text>,
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      key: 'notes',
      render: (text) => text || <Text type="secondary">-</Text>,
    },
  ];

  const totalPaid = payouts.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

  return (
    <div style={{ padding: '24px' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>

        {/* Header Section */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <Title level={4} style={{ margin: 0 }}>
              My Payout History
            </Title>
            <Text type="secondary">
              View your payment transaction history
            </Text>
          </div>

          <Button
            icon={<DownloadOutlined />}
            onClick={handleExport}
            disabled={!payouts.length}
          >
            Export Excel
          </Button>
        </div>

        {/* Info Card */}
        <Card size="small">
          <Descriptions column={{ xxl: 4, xl: 3, lg: 3, md: 3, sm: 2, xs: 1 }}>
            <Descriptions.Item label="My Name">{user?.name || 'Unknown'}</Descriptions.Item>
            <Descriptions.Item label="Mobile">{user?.mobile || '-'}</Descriptions.Item>
            <Descriptions.Item label="Total Paid">
              <Tag color="green" style={{ fontSize: '14px', padding: '4px 8px' }}>
                ₹{totalPaid.toFixed(2)}
              </Tag>
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {/* Data Table */}
          {!screens.md ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {payouts.map(payout => (
                <Card key={payout._id} size="small">
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontSize: 13, color: '#64748b' }}>{new Date(payout.date).toLocaleString()}</span>
                    <Text strong type="success">₹{Number(payout.amount).toFixed(2)}</Text>
                  </div>
                  <div style={{ marginBottom: 4 }}>
                    <span style={{ color: '#64748b', marginRight: 8 }}>Trans ID:</span>
                    <span>{payout.transactionId || '-'}</span>
                  </div>
                  <div>
                    <span style={{ color: '#64748b', marginRight: 8 }}>Notes:</span>
                    <span>{payout.notes || '-'}</span>
                  </div>
                </Card>
              ))}
              {payouts.length === 0 && <Empty description="No Payout History" />}
            </div>
          ) : (
            <Table
              columns={columns}
              dataSource={payouts}
              rowKey={(record) => record._id || record.id || Math.random()}
              loading={payoutsLoading}
              locale={{
                emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No Payout History Found" />
              }}
              pagination={{ pageSize: 10 }}
              scroll={{ x: 'max-content' }}
            />
          )}
      </Space>
    </div>
  );
};

export default PayoutHistory;
